import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  CardContent 
} from "@/components/ui/card";
import { 
  Button 
} from "@/components/ui/button";
import { 
  Calendar, 
  CalendarIcon, 
  Clock, 
  Ban, 
  RefreshCw, 
  CreditCard, 
  AlertCircle,
  Download
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format, parseISO, isToday, isTomorrow, isBefore, addDays } from 'date-fns';
import { useToast } from "@/hooks/use-toast";
import { formatCurrency } from "@/utils/analyticsUtils";
import { supabase } from "@/integrations/supabase/client";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import { ServiceRequest } from './types';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { mapToServiceRequest } from './types';
import jspdf from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useTranslation } from 'react-i18next';

interface CustomerServiceRequestViewProps {
  requestId?: string;
  requestToken?: string;
}

export const CustomerServiceRequestView: React.FC<CustomerServiceRequestViewProps> = ({ 
  requestId: propRequestId,
  requestToken: propRequestToken
}) => {
  const { t } = useTranslation();
  const { requestId: paramRequestId, requestToken: paramRequestToken } = useParams<{ requestId: string; requestToken: string }>();
  const requestId = propRequestId || paramRequestId;
  const requestToken = propRequestToken || paramRequestToken;
  
  const [request, setRequest] = useState<ServiceRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [newDate, setNewDate] = useState<Date | undefined>(undefined);
  const [timeSlots, setTimeSlots] = useState<string[]>([]);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Add state for provider currency
  const [providerCurrency, setProviderCurrency] = useState<string>("KES");
  
  // Add state for provider profile information
  const [providerProfile, setProviderProfile] = useState<any>(null);
  
  // Add state for cancel reason
  const [cancelReason, setCancelReason] = useState<string>("");
  const [customCancelReason, setCustomCancelReason] = useState<string>("");
  const [confirmCancelStep, setConfirmCancelStep] = useState(false);
  const quickReasons = [
    "Change of plans",
    "Found another provider",
    "Service no longer needed",
    "Provider unavailable",
    "Scheduling conflict",
    t('cancelDialog.other')
  ];
  
  // Add function to fetch the provider's currency from profiles table
  const fetchProviderCurrency = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('currency')
        .eq('id', userId)
        .single();
      
      if (error) {
        console.error("Error fetching provider currency:", error);
        return;
      }
      
      if (data && data.currency) {
        console.log("Provider currency:", data.currency);
        setProviderCurrency(data.currency);
      }
    } catch (error) {
      console.error("Error in fetchProviderCurrency:", error);
    }
  };

  // Add function to fetch the service provider's profile
  const fetchProviderProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, company_name, profile_image_url, banner_image_url, currency, first_name, last_name, profession, country')
        .eq('id', userId)
        .single();
      
      if (error) {
        console.error("Error fetching provider profile:", error);
        return;
      }
      
      if (data) {
        console.log("Provider profile:", data);
        setProviderProfile(data);
      }
    } catch (error) {
      console.error("Error in fetchProviderProfile:", error);
    }
  };

  useEffect(() => {
    if (requestId && requestToken) {
      fetchRequestDetails();
    } else {
      setError("Missing request ID or token");
      setLoading(false);
    }
  }, [requestId, requestToken]);

  const fetchRequestDetails = async () => {
    try {
      setLoading(true);
      
      // First, check the structure of the service_requests table
      console.log("Fetching request with ID:", requestId);
      
      // Fetch the request details with validation against the UUID token in the URL
      const { data, error } = await supabase
        .from('service_requests')
        .select(`
          *,
          services (
            id,
            name,
            price,
            duration,
            duration_unit,
            requires_scheduling
          )
        `)
        .eq('id', requestId)
        .single();
      
      if (error) {
        console.error("Error fetching request details:", error);
        
        // Try again without the services join as a fallback
        const { data: basicData, error: basicError } = await supabase
          .from('service_requests')
          .select('*')
          .eq('id', requestId)
          .single();
        
        if (basicError) {
          console.error("Error fetching basic request details:", basicError);
          setError("Couldn't load request details. Please check that the link is correct.");
          setLoading(false);
          return;
        }
        
        console.log("Basic request data (without services):", basicData);
        
        // Now fetch the associated service separately
        const { data: serviceData, error: serviceError } = await supabase
          .from('services')
          .select('id, name, price, duration, duration_unit')
          .eq('id', basicData.service_id)
          .single();
        
        if (serviceError) {
          console.error("Error fetching service details:", serviceError);
          // Continue with minimal data
          const mappedRequest = {
            ...basicData,
            services: {
              id: '',
              name: 'Unknown Service',
              price: 0
            }
          };
          
          // Transform to our ServiceRequest type
          setRequest(mapToServiceRequest(mappedRequest));
          
          // Try to fetch the provider's currency
          fetchProviderCurrency(basicData.user_id);
          
          // Also fetch the provider's profile
          fetchProviderProfile(basicData.user_id);
        } else {
          // Create a combined object with request and service data
          const combinedData = {
            ...basicData,
            services: serviceData
          };
          
          // Transform to our ServiceRequest type
          setRequest(mapToServiceRequest(combinedData));
          
          // Also fetch the provider's currency
          fetchProviderCurrency(data.user_id);
          
          // Also fetch the provider's profile
          fetchProviderProfile(data.user_id);
        }
      } else {
        console.log("Request data with services:", data);
        
        // Validate the URL token against the customer_page_link
        // Format of customer_page_link is "/service-request/{id}/{uuid}"
        const linkParts = data.customer_page_link ? data.customer_page_link.split('/') : [];
        const storedToken = linkParts.length > 0 ? linkParts[linkParts.length - 1] : null;
        
        if (!storedToken || storedToken !== requestToken) {
          // If customer_page_link is missing or malformed, this might be a debugging issue
          console.error("Token validation failed:", {
            storedToken,
            requestToken,
            customer_page_link: data.customer_page_link
          });
          
          // For now, allow access even without token match for debugging
          // In production, you would uncomment the code below to enforce security
          /*
          setError("Invalid or expired link");
          setLoading(false);
          return;
          */
        }
        
        // Transform to our ServiceRequest type
        setRequest(mapToServiceRequest(data));
        
        // Also fetch the provider's currency
        fetchProviderCurrency(data.user_id);
        
        // Also fetch the provider's profile
        fetchProviderProfile(data.user_id);
      }
      
      // If the request has a date, set it as default for rescheduling
      if (data?.scheduled_at) {
        const scheduledDate = new Date(data.scheduled_at);
        // If the date is in the past, default to tomorrow
        if (isBefore(scheduledDate, new Date())) {
          setNewDate(addDays(new Date(), 1));
        } else {
          setNewDate(scheduledDate);
        }
      } else {
        setNewDate(addDays(new Date(), 1));
      }
    } catch (error) {
      console.error("Error:", error);
      setError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeSlots = async (date: Date) => {
    try {
      // Here you would fetch available time slots from your backend
      // For now, we'll simulate some time slots
      const availableSlots = generateMockTimeSlots(date);
      setTimeSlots(availableSlots);
      setSelectedTime(null);
    } catch (error) {
      console.error("Error fetching time slots:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load available time slots"
      });
    }
  };

  // This is a mock function - replace with actual time slot generation logic
  const generateMockTimeSlots = (date: Date): string[] => {
    const slots = [];
    const isForToday = isToday(date);
    const currentHour = new Date().getHours();
    
    // Start from current hour + 1 if today, otherwise from 8 AM
    const startHour = isForToday ? Math.max(8, currentHour + 1) : 8;
    
    for (let hour = startHour; hour <= 18; hour++) {
      slots.push(`${hour}:00`);
      if (hour < 18) slots.push(`${hour}:30`);
    }
    
    return slots;
  };

  const handleCancelRequest = async () => {
    if (!cancelReason && !customCancelReason) {
      toast({
        variant: "destructive",
        title: t('reasonRequired'),
        description: t('reasonRequiredDescription')
      });
      return;
    }
    if (!confirmCancelStep) {
      setConfirmCancelStep(true);
      return;
    }
    try {
      setProcessing(true);
      const reason = cancelReason === t('cancelDialog.other') ? customCancelReason : cancelReason;
      const { error } = await supabase
        .from('service_requests')
        .update({ status: 'cancelled', cancellation_reason: reason })
        .eq('id', requestId);
      if (error) {
        throw error;
      }
      if (request) {
        setRequest({
          ...request,
          status: 'cancelled',
          cancellation_reason: reason
        });
      }
      setConfirmCancelOpen(false);
      setCancelReason("");
      setCustomCancelReason("");
      setConfirmCancelStep(false);
      toast({
        title: t('requestCancelled'),
        description: t('requestCancelledDescription')
      });
    } catch (error) {
      console.error("Error cancelling request:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: t('failedToCancelRequest')
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleRescheduleRequest = async () => {
    if (!newDate || !selectedTime) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please select a date and time"
      });
      return;
    }
    
    try {
      setProcessing(true);
      
      // Parse the selected time
      const [hours, minutes] = selectedTime.split(':').map(Number);
      
      // Set the time on the selected date
      const scheduledDateTime = new Date(newDate);
      scheduledDateTime.setHours(hours, minutes, 0, 0);
      
      const { error } = await supabase
        .from('service_requests')
        .update({ 
          scheduled_at: scheduledDateTime.toISOString(),
          status: 'pending' // Reset to pending so provider can accept again
        })
        .eq('id', requestId);
      
      if (error) {
        throw error;
      }
      
      // Update local state
      if (request) {
        setRequest({
          ...request,
          scheduled_at: scheduledDateTime.toISOString(),
          status: 'pending'
        });
      }
      
      setRescheduleDialogOpen(false);
      
      toast({
        title: "Request Rescheduled",
        description: "Your service has been rescheduled successfully."
      });
    } catch (error) {
      console.error("Error rescheduling request:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to reschedule service"
      });
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = parseISO(dateString);
    if (isToday(date)) {
      return "Today";
    } else if (isTomorrow(date)) {
      return "Tomorrow";
    }
    return format(date, "EEEE, MMM d, yyyy");
  };

  const formatTime = (dateString: string) => {
    const date = parseISO(dateString);
    return format(date, "h:mm a");
  };

  const getStatusBadge = (status: string) => {
    const statusStyles = {
      pending: "bg-yellow-100 text-yellow-800",
      accepted: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
      completed: "bg-blue-100 text-blue-800",
      cancelled: "bg-gray-100 text-gray-800",
    };

    const statusDisplay = {
      pending: "PENDING",
      accepted: "CONFIRMED",
      rejected: "DECLINED",
      completed: "COMPLETED",
      cancelled: "CANCELLED",
    };

    return (
      <Badge className={statusStyles[status as keyof typeof statusStyles]}>
        {statusDisplay[status as keyof typeof statusDisplay] || status.toUpperCase()}
      </Badge>
    );
  };

  const canReschedule = () => {
    return request && ['pending', 'accepted'].includes(request.status);
  };

  const canCancel = () => {
    return request && ['pending', 'accepted'].includes(request.status);
  };

  const canPay = () => {
    return request && !request.paid && ['accepted', 'completed'].includes(request.status);
  };

  const canDownloadReceipt = () => {
    return request && request.paid;
  };

  // Function to get country flag image
  const getCountryFlag = (country: string) => {
    // Map of countries to their ISO codes for flag images
    const countryToCode: Record<string, string> = {
      'afghanistan': 'af',
      'albania': 'al',
      'algeria': 'dz',
      'angola': 'ao',
      'argentina': 'ar',
      'australia': 'au',
      'austria': 'at',
      'bangladesh': 'bd',
      'belgium': 'be',
      'brazil': 'br',
      'cameroon': 'cm',
      'canada': 'ca',
      'china': 'cn',
      'colombia': 'co',
      'egypt': 'eg',
      'ethiopia': 'et',
      'france': 'fr',
      'germany': 'de',
      'ghana': 'gh',
      'greece': 'gr',
      'india': 'in',
      'indonesia': 'id',
      'iran': 'ir',
      'iraq': 'iq',
      'ireland': 'ie',
      'israel': 'il',
      'italy': 'it',
      'japan': 'jp',
      'kenya': 'ke',
      'mexico': 'mx',
      'morocco': 'ma',
      'netherlands': 'nl',
      'nigeria': 'ng',
      'norway': 'no',
      'pakistan': 'pk',
      'poland': 'pl',
      'portugal': 'pt',
      'russia': 'ru',
      'saudi arabia': 'sa',
      'singapore': 'sg',
      'south africa': 'za',
      'south korea': 'kr',
      'spain': 'es',
      'sweden': 'se',
      'switzerland': 'ch',
      'tanzania': 'tz',
      'thailand': 'th',
      'turkey': 'tr',
      'uganda': 'ug',
      'ukraine': 'ua',
      'united arab emirates': 'ae',
      'united kingdom': 'gb',
      'uk': 'gb',
      'united states': 'us',
      'usa': 'us',
      'vietnam': 'vn',
    };

    const lowerCountry = country.toLowerCase().trim();
    let countryCode = '';
    
    // Try exact match
    if (countryToCode[lowerCountry]) {
      countryCode = countryToCode[lowerCountry];
    } else {
      // Try partial match
      for (const [key, code] of Object.entries(countryToCode)) {
        if (lowerCountry.includes(key) || key.includes(lowerCountry)) {
          countryCode = code;
          break;
        }
      }
    }
    
    // If we found a match, return an image, otherwise return a generic globe
    if (countryCode) {
      return (
        <img 
          src={`https://flagcdn.com/16x12/${countryCode}.png`}
          srcSet={`https://flagcdn.com/32x24/${countryCode}.png 2x, https://flagcdn.com/48x36/${countryCode}.png 3x`}
          width="16"
          height="12"
          alt={`${country} flag`}
          className="inline-block mr-1"
        />
      );
    }
    
    // Default to a globe icon if no match is found
    return (
      <span role="img" aria-label="Globe" className="mr-1">🌎</span>
    );
  };

  // Add function to generate PDF receipt
  const generateReceipt = (request: ServiceRequest) => {
    try {
      // Create a new PDF document
      const doc = new jspdf();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // Add border
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.rect(10, 10, pageWidth - 20, pageHeight - 20);
      
      // Add colored header background - Gebeya Purple (#181326)
      doc.setFillColor(24, 19, 38);
      doc.rect(10, 10, pageWidth - 20, 40, 'F');
      
      // Add company logo/header
      doc.setTextColor(255, 255, 255); // White text for header
      doc.setFontSize(32);
      doc.setFont("helvetica", "bold");
      doc.text("RECEIPT", pageWidth / 2, 35, { align: "center" });
      
      // Reset text color to black for the rest of the document
      doc.setTextColor(0, 0, 0);
      
      // Receipt info section (light purple background)
      doc.setFillColor(240, 240, 250);
      doc.rect(20, 60, pageWidth - 40, 40, 'F');
      
      // Add receipt details
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text("Receipt #:", 30, 80);
      doc.text("Date:", 30, 90);
      
      doc.setFont("helvetica", "normal");
      doc.text(request.id.substring(0, 8), 90, 80);
      doc.text(format(new Date(), "MMMM d, yyyy"), 90, 90);
      
      // Provider section (light background)
      doc.setFillColor(240, 240, 250);
      doc.rect(20, 110, (pageWidth - 50) / 2, 70, 'F');
      
      // Customer section (light background)
      doc.setFillColor(240, 240, 250);
      doc.rect((pageWidth / 2) + 10, 110, (pageWidth - 50) / 2, 70, 'F');
      
      // Add service provider details
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Service Provider", 30, 130);
      
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      const providerName = providerProfile?.company_name || 
                           `${providerProfile?.first_name || ''} ${providerProfile?.last_name || ''}`.trim() || 
                           "Service Provider";
      doc.text(providerName, 30, 145);
      
      if (providerProfile?.profession) {
        doc.text(providerProfile.profession, 30, 160);
      }
      
      // Add customer details
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Customer", (pageWidth / 2) + 20, 130);
      
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.text(request.customer_name, (pageWidth / 2) + 20, 145);
      if (request.customer_email) {
        doc.text(request.customer_email, (pageWidth / 2) + 20, 160);
      }
      if (request.customer_phone) {
        doc.text(request.customer_phone, (pageWidth / 2) + 20, 175);
      }
      
      // Service details heading
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Service Details", 30, 200);
      
      // Create a better styled table for service details
      autoTable(doc, {
        startY: 210,
        head: [["Service", "Date", "Amount"]],
        body: [
          [
            request.services.name,
            request.scheduled_at ? format(parseISO(request.scheduled_at), "MMMM d, yyyy") : "Not scheduled",
            formatCurrency(request.services.price, providerCurrency)
          ]
        ],
        theme: "grid",
        styles: { 
          fontSize: 10,
          cellPadding: 8
        },
        headStyles: { 
          fillColor: [24, 19, 38], // Gebeya Purple
          textColor: [255, 255, 255],
          fontStyle: 'bold'
        },
        columnStyles: {
          0: { cellWidth: 'auto' },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 40, halign: 'right' }
        }
      });
      
      // Get the final Y position after the table
      const finalY = (doc as any).lastAutoTable.finalY + 20;
      
      // Payment information section (light background)
      doc.setFillColor(240, 240, 250);
      doc.rect(20, finalY, pageWidth - 40, 60, 'F');
      
      // Add payment information
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Payment Information", 30, finalY + 20);
      
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.text("Payment Status:", 30, finalY + 35);
      doc.text("Payment Date:", 30, finalY + 50);
      
      doc.setFont("helvetica", "bold");
      doc.text("Paid", 110, finalY + 35);
      doc.text(format(new Date(), "MMMM d, yyyy"), 110, finalY + 50);
      
      // Add thank you note in the middle
      doc.setFontSize(10);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(100, 100, 100);
      doc.text("Thank you for your business!", pageWidth / 2, finalY + 80, { align: "center" });
      
      // Add a colored bottom bar - Gebeya purple
      doc.setFillColor(24, 19, 38); // Gebeya Purple
      doc.rect(10, pageHeight - 40, pageWidth - 20, 30, 'F');
      
      // Add total amount text on the blue bar
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("TOTAL:", pageWidth / 2 - 60, pageHeight - 20);
      doc.setFontSize(16);
      doc.text(formatCurrency(request.services.price, providerCurrency), pageWidth / 2 + 30, pageHeight - 20);
      
      // Save the PDF
      doc.save(`receipt-${request.id.substring(0, 8)}.pdf`);
    } catch (error) {
      console.error("Error generating receipt:", error);
      toast({
        variant: "destructive",
        title: "Receipt Generation Failed",
        description: "Failed to generate receipt. Please try again.",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[300px]">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Error</h2>
        <p className="text-gray-600 mb-4">{error}</p>
        <Button variant="outline" onClick={() => window.history.back()}>
          Go Back
        </Button>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="p-6 text-center">
        <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Request Not Found</h2>
        <p className="text-gray-600 mb-4">The service request you're looking for could not be found.</p>
        <Button variant="outline" onClick={() => window.history.back()}>
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-md">
      {/* Service Provider Profile Header */}
      {providerProfile && (
        <div className="relative mb-8">
          {/* Banner */}
          <div className="h-32 bg-gray-200 overflow-hidden">
            {providerProfile.banner_image_url ? (
              <img 
                src={providerProfile.banner_image_url} 
                alt={providerProfile.company_name || providerProfile.first_name || providerProfile.last_name || "Service Provider"} 
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = 'https://placehold.co/800x300/e2e8f0/1e293b?text=Banner';
                }}
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-r from-blue-500 to-purple-600"></div>
            )}
          </div>
          
          {/* Profile Image */}
          <div className="flex justify-center">
            <div className="w-28 h-28 rounded-full border-4 border-white bg-white overflow-hidden shadow-lg -mt-14">
              {providerProfile.profile_image_url ? (
                <img 
                  src={providerProfile.profile_image_url} 
                  alt={providerProfile.company_name || providerProfile.first_name || providerProfile.last_name || "Service Provider"} 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = 'https://placehold.co/200x200/e2e8f0/1e293b?text=Profile';
                  }}
                />
              ) : (
                <div className="w-full h-full bg-gray-300 flex items-center justify-center text-gray-500">
                  <span className="text-2xl">{(providerProfile.company_name || providerProfile.first_name || providerProfile.last_name || "SP").charAt(0)}</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Provider Info */}
          <div className="text-center mt-4">
            <h1 className="text-2xl font-extrabold text-gray-900">{providerProfile.company_name || `${providerProfile.first_name || ''} ${providerProfile.last_name || ''}`.trim() || "Service Provider"}</h1>
            {(providerProfile.first_name || providerProfile.last_name) && (
              <p className="text-lg text-gray-900 mt-1">{`${providerProfile.first_name || ''} ${providerProfile.last_name || ''}`.trim()}</p>
            )}
            <p className="italic text-gray-500 mt-1">{providerProfile.profession || "Service Provider"}</p>
            {providerProfile.country && (
              <div className="flex items-center justify-center mt-2">
                {getCountryFlag(providerProfile.country)}
                <span className="text-base text-gray-700">{providerProfile.country}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Service header with title and status */}
      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 px-4 py-3 border border-b-0 rounded-t-lg">
        <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
          <h2 className="text-xl font-bold text-gray-800">{request.services.name}</h2>
          {getStatusBadge(request.status)}
        </div>
        <p className="text-sm text-gray-600">
          Requested on {formatDate(request.created_at)}
        </p>
        {request.scheduled_at && (
          <div className="mt-2 flex items-center text-indigo-700">
            <Calendar className="w-4 h-4 mr-1 flex-shrink-0" />
            <span className="text-sm font-medium">
              {formatDate(request.scheduled_at)} at {formatTime(request.scheduled_at)}
            </span>
          </div>
        )}
      </div>
      
      {/* Main content */}
      <div className="border border-t-0 border-b-0 bg-white px-4 py-4">
        {/* Your Information */}
        <div className="mb-4">
          <h3 className="text-base font-semibold mb-2 text-gray-700">Your Information</h3>
          <div className="grid gap-1">
            <div className="flex flex-wrap">
              <span className="text-sm w-20 text-gray-500 flex-shrink-0">Name:</span>
              <span className="text-sm font-medium break-words">{request.customer_name}</span>
            </div>
            {request.customer_phone && (
              <div className="flex flex-wrap">
                <span className="text-sm w-20 text-gray-500 flex-shrink-0">Phone:</span>
                <span className="text-sm font-medium break-words">{request.customer_phone}</span>
              </div>
            )}
            {request.customer_email && (
              <div className="flex flex-wrap">
                <span className="text-sm w-20 text-gray-500 flex-shrink-0">Email:</span>
                <span className="text-sm font-medium break-all">{request.customer_email}</span>
              </div>
            )}
            {request.location && (
              <div className="flex flex-wrap">
                <span className="text-sm w-20 text-gray-500 flex-shrink-0">Location:</span>
                <span className="text-sm font-medium break-words">{request.location}</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Service Details */}
        <div className="mb-4">
          <h3 className="text-base font-semibold mb-2 text-gray-700">Service Details</h3>
          <div className="grid gap-1">
            <div className="flex flex-wrap">
              <span className="text-sm w-20 text-gray-500 flex-shrink-0">Price:</span>
              <span className="text-sm font-medium">
                {formatCurrency(request.services.price, providerCurrency)}
              </span>
            </div>
            <div className="flex flex-wrap">
              <span className="text-sm w-20 text-gray-500 flex-shrink-0">Payment:</span>
              <span className={`text-sm font-medium ${request.paid ? "text-green-600" : "text-amber-600"}`}>
                {request.paid ? "Paid" : "Unpaid"}
              </span>
            </div>
            {request.services.duration && (
              <div className="flex flex-wrap">
                <span className="text-sm w-20 text-gray-500 flex-shrink-0">Duration:</span>
                <span className="text-sm font-medium">
                  {request.services.duration}
                  {request.services.duration_unit ? ` ${request.services.duration_unit}` : ''}
                </span>
              </div>
            )}
          </div>
        </div>
        
        {/* Notes (if any) */}
        {request.notes && (
          <div className="mb-4">
            <h3 className="text-base font-semibold mb-2 text-gray-700">Notes</h3>
            <p className="text-sm text-gray-600 whitespace-pre-wrap break-words">{request.notes}</p>
          </div>
        )}

        {/* Status Messages */}
        {request.status === 'cancelled' && (
          <div className="bg-gray-50 rounded-md p-3 border border-gray-200 mb-4">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-gray-500 mt-0.5 mr-2 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-gray-700">This request has been cancelled</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {request.cancellation_reason
                    ? <>Reason: <span className="font-medium">{request.cancellation_reason}</span></>
                    : "If you still need this service, please create a new request."}
                </p>
              </div>
            </div>
          </div>
        )}

        {request.status === 'rejected' && (
          <div className="bg-red-50 rounded-md p-3 border border-red-100 mb-4">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-red-700">This request was declined</h3>
                <p className="text-sm text-red-600 mt-1">
                  The service provider wasn't able to accommodate this request.
                  Please create a new request or try a different provider.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Action Buttons */}
      <div className="p-3 border rounded-b-lg bg-gray-50">
        <div className="grid gap-2">
          {canReschedule() && (
            <Button 
              variant="outline"
              className="justify-center w-full"
              onClick={() => {
                setRescheduleDialogOpen(true);
                if (newDate) fetchTimeSlots(newDate);
              }}
            >
              <Clock className="h-4 w-4 mr-2" />
              Reschedule
            </Button>
          )}
          
          {canCancel() && (
            <Button 
              variant="outline"
              className="justify-center w-full text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
              onClick={() => setConfirmCancelOpen(true)}
            >
              <Ban className="h-4 w-4 mr-2" />
              Cancel Request
            </Button>
          )}
          
          {canPay() && (
            <Button 
              className="justify-center w-full bg-green-600 hover:bg-green-700 text-white"
              onClick={() => {
                // This would be implemented with your payment solution
                toast({
                  title: "Payment Feature",
                  description: "This feature will be available soon"
                });
              }}
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Pay Online
            </Button>
          )}

          {canDownloadReceipt() && (
            <Button 
              variant="outline"
              className="justify-center w-full bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200"
              onClick={() => generateReceipt(request)}
            >
              <Download className="h-4 w-4 mr-2" />
              Download Receipt
            </Button>
          )}
        </div>
      </div>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={confirmCancelOpen} onOpenChange={setConfirmCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('cancelDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('cancelDialog.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {quickReasons.map((reason) => (
                <Button
                  key={reason}
                  variant={cancelReason === reason ? "default" : "outline"}
                  className={`text-xs px-3 py-1 ${cancelReason === reason ? 'border-2 border-red-600 ring-2 ring-red-200' : ''}`}
                  onClick={() => setCancelReason(reason)}
                >
                  {reason}
                </Button>
              ))}
            </div>
            {(cancelReason === t('cancelDialog.other') || cancelReason === "") && (
              <textarea
                className="w-full border rounded p-2 text-sm"
                placeholder={t('cancelDialog.enterCustomReason')}
                value={customCancelReason}
                onChange={e => setCustomCancelReason(e.target.value)}
                rows={2}
              />
            )}
            {confirmCancelStep && (
              <div className="text-red-700 bg-red-50 border border-red-200 rounded p-2 text-sm mt-2">
                <strong>Warning:</strong> {t('cancelDialog.warningMessage')}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmCancelOpen(false); setConfirmCancelStep(false); }} disabled={processing}>
              {t('cancelDialog.keepRequest')}
            </Button>
            <Button 
              variant="destructive"
              className="font-semibold bg-red-600 text-white hover:bg-red-700 border-red-600"
              onClick={handleCancelRequest}
              disabled={processing || (!cancelReason && !customCancelReason)}
            >
              {processing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
              {t('cancelDialog.confirmCancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={rescheduleDialogOpen} onOpenChange={setRescheduleDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reschedule Service</DialogTitle>
            <DialogDescription>
              Please select a new date and time for your service.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {/* Date Picker */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {newDate ? format(newDate, "PPP") : "Select a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <CalendarComponent
                    mode="single"
                    selected={newDate}
                    onSelect={(date) => {
                      setNewDate(date);
                      if (date) fetchTimeSlots(date);
                    }}
                    disabled={(date) => 
                      date < new Date() || // Can't select dates in the past
                      date.getDay() === 0 || // Can't select Sundays 
                      date.getDay() === 6 // Can't select Saturdays
                    }
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Time Picker */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Time</label>
              <Select value={selectedTime || ""} onValueChange={setSelectedTime}>
                <SelectTrigger>
                  <SelectValue placeholder={t('selectATime')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Available Times</SelectLabel>
                    {timeSlots.length > 0 ? (
                      timeSlots.map((time) => (
                        <SelectItem key={time} value={time}>
                          {time}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="none" disabled>
                        No available times
                      </SelectItem>
                    )}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setRescheduleDialogOpen(false)}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleRescheduleRequest}
              disabled={!newDate || !selectedTime || processing}
            >
              {processing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
              Reschedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerServiceRequestView; 